import { Error } from '@mui/icons-material';
import { Box, Dialog, DialogContent, Typography, useTheme } from '@mui/material';
import { useCallback, useContext } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import { ContactProps } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';
import { ProgramNames } from '@/models/Program';

const DisplayContactErrors = ({
  header,
  errorFields,
  requiredContactFieldLabels,
}: {
  header: string;
  errorFields: Array<keyof ContactProps>;
  requiredContactFieldLabels: object;
}) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant='h3'>{header}</Typography>

      {errorFields.map((item: string) => {
        return (
          <div key={`${item}-error`} style={{ color: theme.palette.error.main, margin: '5px 0px' }}>
            <Error
              style={{
                position: 'relative',
                width: '20px',
                height: '20px',
                top: '4px',
                marginRight: '10px',
              }}
            ></Error>
            {requiredContactFieldLabels[item as keyof typeof requiredContactFieldLabels]}
          </div>
        );
      })}
    </Box>
  );
};

function IntakeErrorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const theme = useTheme();
  const { selectedProgramInfo } = useContext(PartnerIntakeContext);
  const { locale, translatedText } = useContext(LocaleContext);

  const {
    isNewPartner,
    primaryContactFieldsInError,
    shippingContactFieldsInError,
    partnerFieldsInError,
    intakeLabels,
  } = useContext(PartnerIntakeContext);

  const requiredContactFieldLabels = {
    firstName: translatedText.contact.labelFirstName,
    lastName: translatedText.contact.labelLastNameLabel,
    phone: translatedText.contact.labelPhoneNumber,
    streetAddress1: translatedText.contact.labelStreetAddressLabel,
    city: translatedText.contact.labelCity,
    countyId: 'County',
    stateId: 'State',
    countryId: translatedText.contact.labelCountry,
    zipCode: translatedText.contact.labelZipCode,
    title: translatedText.signup.labelTitle,
    departmentTypeId: 'Home Department',
    departmentOther: 'Please specify department',
    contactSubTypeId: translatedText.signup.labelTitle,
  };

  const handleModalClose = useCallback(() => {
    if (onClose != null) {
      onClose();
    }
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleModalClose}>
      <DialogTitleWithClose onClose={handleModalClose}>
        There Were Problems With Your Form
      </DialogTitleWithClose>
      <DialogContent>
        <Typography>{translatedText.signup.errorMessageStart}</Typography>

        {primaryContactFieldsInError.length > 0 && (
          <DisplayContactErrors
            header={translatedText.signup.errorPrimaryContactHeader}
            errorFields={primaryContactFieldsInError}
            requiredContactFieldLabels={requiredContactFieldLabels}
          />
        )}

        {partnerFieldsInError.length > 0 && (
          <Box>
            <Typography variant='h3'>
              {selectedProgramInfo.id === ProgramNames.TreeCitiesWorld && locale !== 'enUS'
                ? translatedText.signup.errorPartnerHeader
                : `${selectedProgramInfo?.partnerTerm} ${translatedText.signup.errorPartnerHeader}`}
            </Typography>

            {partnerFieldsInError.map((item: keyof PartnerProps) => {
              return (
                <div
                  key={`${item}-error`}
                  style={{ color: theme.palette.error.main, margin: '5px 0px' }}
                >
                  <Error
                    style={{
                      position: 'relative',
                      width: '20px',
                      height: '20px',
                      top: '4px',
                      marginRight: '10px',
                    }}
                  ></Error>
                  {intakeLabels[item]}
                </div>
              );
            })}
          </Box>
        )}

        {shippingContactFieldsInError.length > 0 && isNewPartner === true && (
          <DisplayContactErrors
            header={translatedText.signup.errorShippingContactHeader}
            errorFields={shippingContactFieldsInError}
            requiredContactFieldLabels={requiredContactFieldLabels}
          />
        )}

        <p style={{ marginTop: '20px' }}>{translatedText.signup.errorMessageEnd}</p>
      </DialogContent>
    </Dialog>
  );
}

export default IntakeErrorModal;
